import { MomentumModalV4 } from "@circle-react-shared/ModalsV2/MomentumModalV4";
import { Button } from "@circleco/compass/components/Button";
import { Typography } from "@circleco/compass/components/Typography";
import type { MouseEvent } from "react";

import { t } from "@/i18n-js/instance";
import type { CommunityMember } from "@/react/types/CommunityMember";

import { SanitizeHTMLString } from "../../../uikit/SanitizeContent";
import { MemberCardImage } from "../CardView/MemberCardImage";

const i18nBase = "member_directory.accept_connection_momentum_modal";

interface MemberPreviewCardProps {
  avatarUrl: string;
  headline: string;
  name: string;
}

const MemberPreviewCard = ({
  name,
  avatarUrl,
  headline,
}: MemberPreviewCardProps) => (
  <div className="flex w-40 flex-col items-center rounded-xl border px-6 py-4 text-center">
    <div style={{ aspectRatio: "1 / 1" }} className="mb-3 h-14">
      <MemberCardImage name={name} src={avatarUrl} className="!rounded-full" />
    </div>
    <div className="w-full">
      <Typography variant="label-sm" truncate>
        {name}
      </Typography>
    </div>
    <div className="w-full">
      <Typography variant="body-sm" color="secondary" truncate>
        {headline}
      </Typography>
    </div>
  </div>
);

interface AcceptConnectionMomentumModalV4Props {
  currentMemberAvatarUrl: string;
  currentMemberHeadline: string;
  currentMemberName: string;
  isLoading: boolean;
  isMessagingEnabled: boolean;
  isOpen: boolean;
  onClose: () => void;
  onMessage: () => void;
  onViewProfile: (event: MouseEvent<HTMLButtonElement>) => void;
  recipient: CommunityMember;
}

export const AcceptConnectionMomentumModalV4 = ({
  isOpen,
  isLoading,
  recipient,
  currentMemberName,
  currentMemberAvatarUrl,
  currentMemberHeadline,
  isMessagingEnabled,
  onClose,
  onMessage,
  onViewProfile,
}: AcceptConnectionMomentumModalV4Props) => (
  <MomentumModalV4 isOpen={isOpen} onClose={onClose} title="" description="">
    <div className="flex w-full flex-col items-center">
      <div className="flex flex-row gap-x-2">
        <MemberPreviewCard
          name={currentMemberName}
          avatarUrl={currentMemberAvatarUrl}
          headline={currentMemberHeadline}
        />
        <MemberPreviewCard
          name={recipient.name ?? ""}
          avatarUrl={recipient.avatar_url ?? ""}
          headline={recipient.headline ?? ""}
        />
      </div>

      <div className="mt-10 flex w-full flex-col items-center gap-y-3">
        <Typography variant="heading-md">{t(`${i18nBase}.title`)}</Typography>
        <Typography variant="label-md" color="secondary">
          <SanitizeHTMLString
            content={t(`${i18nBase}.body`, { recipient: recipient.name })}
            className="block w-full text-center"
          />
        </Typography>
      </div>

      <div className="mt-12 flex w-full flex-col">
        {isMessagingEnabled ? (
          <Button
            variant="outline"
            size="lg"
            fullWidth
            startIcon="message"
            onClick={onMessage}
            data-testid="chat-button"
            loading={isLoading}
          >
            {t(`${i18nBase}.cta`, { recipient: recipient.name })}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="lg"
            fullWidth
            startIcon="message"
            onClick={onViewProfile}
            data-testid="view-profile-button"
          >
            {t(`${i18nBase}.view_profile_cta`)}
          </Button>
        )}
      </div>
    </div>
  </MomentumModalV4>
);
